import { useAccount } from "@azure/msal-react";
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Flex,
    Group,
    rem,
    Stack,
    Text,
    Textarea,
    useMantineColorScheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAdjustments, IconX } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { IndexableType } from "dexie";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineSend } from "react-icons/ai";
import { IoStopCircle } from "react-icons/io5";
import { FollowUpSuggestions } from "src/components/FollowUpSuggestions";
import { MessageItem } from "src/components/MessageItem";
import { sendFeedbackToApi } from "src/components/MessageUtils";
import { db, type MessageEntity } from "src/db";
import {
    abortCurrentStream,
    useApiStreamingState,
    useChatCompletion,
} from "src/hooks/useChatCompletion";
import { useMetadata } from "src/hooks/useMetadata";
import { STORAGE_KEYS, useStorage } from "src/hooks/useStorage";
import { useUser, useUserDatasources } from "src/hooks/useUser";
import {
    type CopilotContextData,
    getContextualSuggestions as getContextSuggestions,
} from "src/utils/contextTemplates";

const APP_TITLE = import.meta.env.VITE_APP_TITLE ?? "Knowledge Search";

/**
 * Convert context tags to CopilotContextData for template generation
 */
function contextTagsToContextData(contextTags?: ContextTag[]): CopilotContextData | undefined {
    if (!contextTags || contextTags.length === 0) {
        return undefined;
    }

    const context: CopilotContextData = {};

    for (const tag of contextTags) {
        switch (tag.type) {
            case "equipment":
                context.equipmentId = tag.value;
                break;
            case "alert":
                context.alertDescription = tag.value;
                break;
            case "location":
                context.location = tag.value;
                break;
            default:
                // Handle additional tags like "Type"
                if (tag.label === "Type") {
                    context.equipmentType = tag.value;
                }
        }
    }

    return context;
}

/**
 * Generate context-aware suggestions based on the provided context tags
 */
function getContextualSuggestions(contextTags?: ContextTag[]): string[] {
    const contextData = contextTagsToContextData(contextTags);
    return getContextSuggestions(contextData);
}

/** Context tag to display near the input */
export interface ContextTag {
    label: string;
    value: string;
    type?: "equipment" | "alert" | "location" | "default";
}

export function ChatRouteChild({
    chatId,
    messages,
    files,
    isCompact,
    isChatWithDocument,
    hideHeader: _hideHeader,
    metadataFilter,
    customStyles,
    initialContent,
    onContentChange,
    contextTags,
    onClearContext,
    onChatCreated,
    disabled,
}: {
    readonly chatId?: IndexableType;
    readonly messages?: MessageEntity[];
    readonly files?: string[];
    readonly isCompact?: boolean;
    readonly isChatWithDocument?: boolean;
    readonly hideHeader?: boolean;
    readonly metadataFilter?: { key: string; value: string }[];
    readonly customStyles?: Record<string, string | number>;
    /** Initial content to pre-fill in the input field */
    readonly initialContent?: string;
    /** Callback when content changes (for controlled input from parent) */
    readonly onContentChange?: (content: string) => void;
    /** Context tags to display near the input */
    readonly contextTags?: ContextTag[];
    /** Callback when a context tag is removed (receives label) or all context is cleared */
    readonly onClearContext?: (label?: string) => void;
    /** Callback when a new chat is created (returns the new chat ID) */
    readonly onChatCreated?: (chatId: string) => void;
    /** Disable input (e.g., while history is loading) */
    readonly disabled?: boolean;
}) {
    const navigate = useNavigate();
    const { chatMetadata, addChatMetadata } = useMetadata();
    const { data: userData } = useUser();
    const { data: userDatasourceDetails } = useUserDatasources();
    const account = useAccount();
    const { colorScheme } = useMantineColorScheme();

    // User preferences from storage (uses polling fallback in widget/copilot mode)
    const [isForbidden, _] = useStorage(STORAGE_KEYS.IS_FORBIDDEN, false);
    const [chatPreferences, _setChatPreferences] = useStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>(STORAGE_KEYS.CHAT_PREFERENCES, {
        selectedProductLines: [],
        selectedDatasources: [],
    });
    const [selectedOrganization, _setSelectedOrganization] = useStorage<string>(
        STORAGE_KEYS.SELECTED_ORGANIZATION,
        "",
    );
    // Check if preferences exist and navigate to questionnaire if needed
    // Skip questionnaire redirect in copilot mode (embedded in iframe)
    const isCopilotMode =
        window.location.hash.includes("/copilot") ||
        window.location.search.includes("mode=copilot");

    // In copilot/widget mode, localStorage is partitioned (cross-origin iframe context).
    // Always use backend user_preferences as the authoritative source for datasource display.
    const effectiveChatPreferences = useMemo(() => {
        if (isCopilotMode && userData?.info?.user_preferences?.datasources?.length) {
            return {
                selectedProductLines: [userData.info.user_preferences.product_line || "all"],
                selectedDatasources: userData.info.user_preferences.datasources,
            };
        }
        return chatPreferences;
    }, [isCopilotMode, chatPreferences, userData]);

    const reports =
        JSON.parse(localStorage.getItem("datasourceDetails") || "[]")
            .filter((datasource) => datasource?.enable_chat_with_document)
            .map((datasource) => datasource.id) || [];

    useEffect(() => {
        if (!chatPreferences.selectedProductLines.length && !isCopilotMode) {
            navigate({
                to: "/questionnaire",
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                search: { source: document.location.pathname.replace(/\/{2,}/g, "/") } as any,
            });
        }
    }, [chatPreferences, navigate, isCopilotMode]);

    useEffect(() => {
        if (files && files?.length > 0) {
            for (const file of files) {
                if (
                    chatMetadata.find((metadata) => {
                        return reports.includes(metadata.key) && file.startsWith(metadata.value);
                    })
                )
                    return;
                addChatMetadata(file.split("_")[0], "file");
            }
        }
    }, [files, chatMetadata, addChatMetadata, reports]);
    const chatCompletion = useChatCompletion(chatId, isChatWithDocument);
    const [isInitialLoading, _setIsInitialLoading] = useState(false);
    const isStreaming = useApiStreamingState(chatId?.toString());
    const isLoading = chatCompletion.isloading || isInitialLoading || isStreaming;

    const [content, setContent] = useState(initialContent || "");
    const ref = useRef<HTMLTextAreaElement>(null);

    // Store callback in ref to avoid triggering effects when callback reference changes
    // This prevents React Error #185 (Maximum update depth exceeded) when the parent
    // component recreates the callback on state changes
    const onContentChangeRef = useRef(onContentChange);
    useEffect(() => {
        onContentChangeRef.current = onContentChange;
    }, [onContentChange]);

    // Update content when initialContent prop changes (for copilot suggested questions)
    useEffect(() => {
        if (initialContent) {
            setContent((prevContent) => {
                if (prevContent !== initialContent) {
                    // Focus the input and move cursor to end
                    if (ref.current) {
                        ref.current.focus();
                        ref.current.setSelectionRange(initialContent.length, initialContent.length);
                    }
                    return initialContent;
                }
                return prevContent;
            });
        }
    }, [initialContent]);

    // Notify parent when content changes (using ref to prevent infinite loops)
    useEffect(() => {
        onContentChangeRef.current?.(content);
    }, [content]);

    const isMobile = useMediaQuery("(max-width: 768px)") ?? false;
    const [showAllDatasources, setShowAllDatasources] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    const isCollapsed = isMobile && !showAllDatasources;

    const [selectedQuestions, setSelectedQuestions] = useState<string[]>(
        isChatWithDocument ? [] : getContextualSuggestions(contextTags),
    );
    const isNewChat = !messages?.length;
    const showSuggestions =
        isNewChat && selectedQuestions.length > 0 && !content.trim() && !inputFocused;
    const chat = useLiveQuery(
        () => (chatId == null ? undefined : db.chats.where("id").equals(chatId).first()),
        [chatId],
    );
    useEffect(() => {
        if (chatId == null) return;

        const loadMessages = async () => {
            const suggestedMessages = chat?.suggestedMessages ?? [];
            setSelectedQuestions(suggestedMessages);
        };

        loadMessages();
    }, [chatId, chat]);

    // Update suggestions when context tags change
    useEffect(() => {
        // Only update if there's no active chat (new chat state)
        if (!chatId && !isChatWithDocument) {
            setSelectedQuestions(getContextualSuggestions(contextTags));
        }
    }, [contextTags, chatId, isChatWithDocument]);

    /**
     * Build a context-enriched message that includes context information
     * This allows the backend/LLM to understand the equipment context
     * Context is appended at the END so it doesn't appear in message previews
     */
    const buildContextEnrichedMessage = (userMessage: string, tags?: ContextTag[]): string => {
        if (!tags || tags.length === 0) {
            return userMessage;
        }

        // Build context section
        const contextParts: string[] = [];
        for (const tag of tags) {
            contextParts.push(`${tag.label}: ${tag.value}`);
        }

        // Format: User question first, then context at the end
        // This way the message preview shows the actual question, not the context
        const contextSuffix = `\n\n[Context: ${contextParts.join(", ")}]`;
        return userMessage + contextSuffix;
    };

    const submit = async () => {
        const newContent = content;
        setContent("");
        setSelectedQuestions([]);

        // Enrich message with context if available
        const enrichedMessage = buildContextEnrichedMessage(newContent, contextTags);

        await chatCompletion.sendMessage(
            enrichedMessage,
            chatId,
            metadataFilter,
            // Callback for when a new chat is created (called before streaming starts)
            !chatId && onChatCreated
                ? (newChatId) => onChatCreated(newChatId.toString())
                : undefined,
            effectiveChatPreferences.selectedDatasources,
        );
    };

    // Use either isCompact or isChatWithDocument for compact mode
    const isCompactMode = isCompact || isChatWithDocument;

    useEffect(() => {
        try {
            let organizationName = selectedOrganization;
            if (!organizationName || organizationName === "all") {
                organizationName = "Default Filters";
            }
            const newTitle =
                organizationName === "Default Filters"
                    ? APP_TITLE
                    : `${APP_TITLE} - ${organizationName}`;
            document.title = newTitle;
        } catch (error) {
            console.error("Error updating chat title:", error);
        }
    }, [selectedOrganization]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: messages?.length || 0,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 150,
        overscan: 5,
    });

    useEffect(() => {
        if (!scrollRef.current || !messages?.length) return;

        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages?.length]);

    return (
        <Flex
            direction="column"
            style={{
                height: isCompact ? "100vh" : "calc(100vh - 100px)", // No header in compact mode
            }}
        >
            {/* Scrollable content area (title + messages) */}
            <Box
                ref={scrollRef}
                style={{
                    flexGrow: 1,
                    overflow: "auto",
                    padding: isCompact ? "12px" : undefined,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Messages Area */}
                <Box
                    ref={messagesContainerRef}
                    style={(theme) => ({
                        padding: isCompactMode ? "0" : `0 ${theme.spacing.xl}`,
                        paddingBottom: isCompactMode ? rem(40) : theme.spacing.md,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                    })}
                >
                    {messages && messages.length > 0 ? (
                        <div
                            style={{
                                height: `${virtualizer.getTotalSize()}px`,
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {virtualizer.getVirtualItems().map((virtualRow, _index) => {
                                const message = messages[virtualRow.index];
                                const isLastMessage = virtualRow.index === messages.length - 1;
                                const isAssistantMessage = message.role === "assistant";
                                const showStreaming =
                                    isStreaming && isLastMessage && isAssistantMessage;

                                return (
                                    <Stack
                                        key={message.id.toString()}
                                        ref={virtualizer.measureElement}
                                        data-index={virtualRow.index}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            transform: `translateY(${virtualRow.start}px)`,
                                            willChange: showStreaming ? "contents" : "transform",
                                        }}
                                        gap="xs"
                                    >
                                        <MessageItem
                                            isCompact={isCompactMode}
                                            sendFeedbackToApi={sendFeedbackToApi}
                                            retry={async (newMessage: string) => {
                                                setContent("");
                                                setSelectedQuestions([]);
                                                // Note: retry uses the original message which may already have context
                                                await chatCompletion.sendMessage(
                                                    newMessage,
                                                    chatId,
                                                    metadataFilter,
                                                    undefined,
                                                    effectiveChatPreferences.selectedDatasources,
                                                );
                                            }}
                                            message={message}
                                            nextAssistantMessage={
                                                messages[virtualRow.index + 1] &&
                                                virtualRow.index > 1
                                                    ? messages[virtualRow.index + 1]
                                                    : undefined
                                            }
                                            inProgress={showStreaming}
                                            followUpSuggestions={undefined}
                                            isLastMessage={false}
                                        />
                                    </Stack>
                                );
                            })}
                        </div>
                    ) : (
                        <Flex
                            direction="column"
                            flex={1}
                            justify="space-between"
                            style={{ marginTop: isCompact ? 40 : 0 }}
                        >
                            <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                style={{ opacity: 0.7 }}
                            >
                                <Text size="xl" fw={500} c="black">
                                    Start a conversation
                                </Text>
                                <Text size="md" c="black" ta="center" mt="sm">
                                    Choose one of the suggested questions below or type your own
                                </Text>
                            </Flex>
                            {showSuggestions && (
                                <FollowUpSuggestions
                                    title="Suggested questions"
                                    suggestions={selectedQuestions}
                                    onSuggestionClick={(question) => {
                                        if (!isForbidden) {
                                            setContent(question);
                                        }
                                    }}
                                />
                            )}
                        </Flex>
                    )}

                    {/* Render suggestions outside the virtual container to avoid clipping */}
                    {messages && messages.length > 0 && selectedQuestions.length > 0 && (
                        <Box
                            style={(theme) => ({
                                align: "center",
                                padding: isCompactMode ? "0" : `0 ${theme.spacing.xl}`,
                                paddingBottom: isCompactMode ? rem(24) : undefined,
                            })}
                        >
                            <FollowUpSuggestions
                                suggestions={selectedQuestions}
                                onSuggestionClick={(suggestion: string) => {
                                    setContent("");
                                    setSelectedQuestions([]);
                                    // Enrich suggestion with context if available
                                    const enrichedSuggestion = buildContextEnrichedMessage(
                                        suggestion,
                                        contextTags,
                                    );
                                    chatCompletion.sendMessage(
                                        enrichedSuggestion,
                                        chatId,
                                        metadataFilter,
                                        undefined,
                                        effectiveChatPreferences.selectedDatasources,
                                    );
                                }}
                                maxSuggestions={isCompactMode ? 1 : undefined}
                                compact={isCompactMode}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Sticky Input Area */}
            <Box
                py="lg"
                px="xl"
                style={(theme) => ({
                    flexShrink: 0,
                    backgroundColor:
                        colorScheme === "dark" ? theme.colors.dark[9] : theme.colors.gray[0],
                    boxShadow: "none",
                    zIndex: 10,
                    ...(customStyles ?? {}),
                })}
            >
                <Stack gap="0">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (chatCompletion.isloading || isLoading || isForbidden || disabled)
                                return false;
                            submit();
                            (document.activeElement as HTMLElement)?.blur();
                            return false;
                        }}
                    >
                        {/* Context pills - shown when host app sets context */}
                        {contextTags && contextTags.length > 0 && (
                            <Group gap="xs" mb="xs">
                                {contextTags.map((tag) => (
                                    <Badge
                                        key={`${tag.label}-${tag.value}`}
                                        size="sm"
                                        variant="light"
                                        color={
                                            tag.type === "equipment"
                                                ? "purple"
                                                : tag.type === "alert"
                                                  ? "red"
                                                  : tag.type === "location"
                                                    ? "blue"
                                                    : "gray"
                                        }
                                        rightSection={
                                            onClearContext && (
                                                <ActionIcon
                                                    size="xs"
                                                    variant="transparent"
                                                    color="gray"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onClearContext(tag.label);
                                                    }}
                                                    style={{ marginLeft: 2 }}
                                                >
                                                    <IconX size={10} />
                                                </ActionIcon>
                                            )
                                        }
                                        styles={{
                                            root: {
                                                paddingRight: onClearContext ? 4 : undefined,
                                            },
                                        }}
                                    >
                                        <Text size="xs" fw={500} span>
                                            {tag.label}:
                                        </Text>{" "}
                                        {tag.value}
                                    </Badge>
                                ))}
                            </Group>
                        )}

                        {/* Datasource preference badges */}
                        {effectiveChatPreferences.selectedDatasources.length > 0 && (
                            <Group
                                gap="xs"
                                mb="xs"
                                wrap={isCollapsed ? "nowrap" : "wrap"}
                                align="center"
                                style={{ overflow: "hidden" }}
                            >
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="violet"
                                    leftSection={<IconAdjustments size={13} />}
                                    onClick={() =>
                                        isCompact
                                            ? window.open("/#/questionnaire", "_blank")
                                            : navigate({ to: "/questionnaire" })
                                    }
                                    px={6}
                                    style={{ flexShrink: 0 }}
                                >
                                    Edit Preferences
                                </Button>
                                {(() => {
                                    const productLine =
                                        effectiveChatPreferences.selectedProductLines[0];
                                    const showProductLine = productLine && productLine !== "all";
                                    const sortedDatasources = [
                                        ...effectiveChatPreferences.selectedDatasources,
                                    ].sort((a, b) => {
                                        const nameA =
                                            userDatasourceDetails?.find((d) => d.id === a)?.name ??
                                            a;
                                        const nameB =
                                            userDatasourceDetails?.find((d) => d.id === b)?.name ??
                                            b;
                                        return nameA.localeCompare(nameB);
                                    });
                                    const VISIBLE_COUNT = 2;
                                    const visibleDatasources = isCollapsed
                                        ? sortedDatasources.slice(0, VISIBLE_COUNT)
                                        : sortedDatasources;
                                    const hiddenCount = sortedDatasources.length - VISIBLE_COUNT;

                                    return (
                                        <>
                                            {showProductLine && (
                                                <Badge
                                                    size="sm"
                                                    variant="filled"
                                                    color="teal"
                                                    radius="sm"
                                                    style={{ flexShrink: 0 }}
                                                >
                                                    {productLine}
                                                </Badge>
                                            )}
                                            {visibleDatasources.map((dsId) => {
                                                const ds = userDatasourceDetails?.find(
                                                    (d) => d.id === dsId,
                                                );
                                                return (
                                                    <Badge
                                                        key={dsId}
                                                        size="sm"
                                                        variant="light"
                                                        color="violet"
                                                        radius="sm"
                                                    >
                                                        {ds?.name ?? dsId}
                                                    </Badge>
                                                );
                                            })}
                                            {isCollapsed && hiddenCount > 0 && (
                                                <Badge
                                                    size="sm"
                                                    variant="outline"
                                                    color="violet"
                                                    radius="sm"
                                                    style={{ flexShrink: 0, cursor: "pointer" }}
                                                    onClick={() => setShowAllDatasources(true)}
                                                >
                                                    +{hiddenCount} more
                                                </Badge>
                                            )}
                                            {isMobile && !isCollapsed && (
                                                <Badge
                                                    size="sm"
                                                    variant="outline"
                                                    color="gray"
                                                    radius="sm"
                                                    style={{ flexShrink: 0, cursor: "pointer" }}
                                                    onClick={() => setShowAllDatasources(false)}
                                                >
                                                    show less
                                                </Badge>
                                            )}
                                        </>
                                    );
                                })()}
                            </Group>
                        )}

                        {isCompact ? (
                            /* Compact mode: textarea with absolute positioned button */
                            <Box style={{ position: "relative", width: "100%" }}>
                                <Textarea
                                    disabled={isForbidden || disabled}
                                    ref={ref}
                                    value={content}
                                    style={{ width: "100%" }}
                                    placeholder={
                                        disabled
                                            ? "Loading chat history..."
                                            : "How can I help you today?"
                                    }
                                    id="chat-input"
                                    autosize
                                    minRows={2}
                                    maxRows={6}
                                    onChange={(event) => setContent(event.currentTarget.value)}
                                    onFocus={() => setInputFocused(true)}
                                    onBlur={() => setTimeout(() => setInputFocused(false), 150)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" && !event.shiftKey) {
                                            event.preventDefault();
                                            if (
                                                !chatCompletion.isloading &&
                                                !isLoading &&
                                                !isForbidden &&
                                                !disabled &&
                                                content.trim()
                                            ) {
                                                submit();
                                            }
                                        }
                                    }}
                                    styles={(theme) => ({
                                        input: {
                                            borderRadius: theme.radius.sm,
                                            paddingRight: rem(50),
                                            paddingBottom: rem(8),
                                            fontSize: rem(13),
                                        },
                                    })}
                                />
                                <Button
                                    aria-label={isStreaming ? "Stop" : "Submit"}
                                    data-testid={isStreaming ? "stop-button" : "send-button"}
                                    variant="filled"
                                    color={isStreaming ? "red" : undefined}
                                    onClick={isStreaming ? abortCurrentStream : submit}
                                    disabled={
                                        !isStreaming &&
                                        (chatCompletion.isloading ||
                                            isLoading ||
                                            !content ||
                                            isForbidden ||
                                            disabled)
                                    }
                                    style={{
                                        position: "absolute",
                                        bottom: rem(8),
                                        right: rem(8),
                                        fontSize: "1rem",
                                        minWidth: rem(32),
                                        height: rem(32),
                                        padding: 0,
                                    }}
                                >
                                    {isStreaming ? (
                                        <IoStopCircle size={18} />
                                    ) : (
                                        <AiOutlineSend size={16} />
                                    )}
                                </Button>
                            </Box>
                        ) : (
                            /* Main app: original side-by-side layout */
                            <Flex gap="sm" align="center" style={{ position: "relative" }}>
                                <Flex style={{ flex: 1 }}>
                                    <Textarea
                                        disabled={isForbidden || disabled}
                                        ref={ref}
                                        value={content}
                                        style={{ flex: 1, width: "100%" }}
                                        placeholder={`Hello, ${account?.name || "there"}!
How can I assist with your technical work today? Please share the full context for a precise response.
The more details you provide, the better I can assist you.`}
                                        id="chat-input"
                                        autosize
                                        minRows={3}
                                        maxRows={8}
                                        onChange={(event) => setContent(event.currentTarget.value)}
                                        onFocus={() => setInputFocused(true)}
                                        onBlur={() => setTimeout(() => setInputFocused(false), 150)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" && !event.shiftKey) {
                                                event.preventDefault();
                                                if (
                                                    !chatCompletion.isloading &&
                                                    !isLoading &&
                                                    !isForbidden &&
                                                    !disabled &&
                                                    content.trim()
                                                ) {
                                                    submit();
                                                }
                                            }
                                        }}
                                        styles={(theme) => ({
                                            input: {
                                                borderRadius: theme.radius.sm,
                                            },
                                        })}
                                    />
                                </Flex>
                                <Button
                                    aria-label={isStreaming ? "Stop" : "Submit"}
                                    data-testid={isStreaming ? "stop-button" : "send-button"}
                                    variant="filled"
                                    color={isStreaming ? "red" : undefined}
                                    onClick={isStreaming ? abortCurrentStream : submit}
                                    disabled={
                                        !isStreaming &&
                                        (chatCompletion.isloading ||
                                            isLoading ||
                                            !content ||
                                            isForbidden ||
                                            disabled)
                                    }
                                    style={{
                                        fontSize: "1.2rem",
                                    }}
                                >
                                    {isStreaming ? <IoStopCircle size={20} /> : <AiOutlineSend />}
                                </Button>
                            </Flex>
                        )}
                    </form>
                    <Text
                        fw="bold"
                        c="gray.7"
                        mt=".5rem"
                        fz="xs"
                        ta="center"
                        style={{
                            maxWidth: "80%",
                            margin: "0 auto",
                            "@media (maxWidth: 768px)": {
                                fontSize: "10px",
                                lineHeight: 1.2,
                                marginTop: "0.25rem",
                            },
                        }}
                    >
                        Knowledge Search uses Generative AI. Please verify the answers for accuracy
                        before using them.
                    </Text>
                </Stack>
            </Box>
        </Flex>
    );
}
